/**
 * Format XRP price with proper decimal handling
 * - Integers shown without decimals: 199.0 → "199 XRP"
 * - Decimals shown without trailing zeros: 199.5000 → "199.5 XRP", 199.99 → "199.99 XRP"
 * - No thousand separators for clarity: 1000 → "1000 XRP"
 * - Rounds to 6 decimal places to avoid floating point precision issues
 * - Very small prices (< 0.000001) shown with more precision
 */
export function formatPrice(price: number | null | undefined): string {
  if (price === null || price === undefined) return 'N/A';
  if (price === 0) return '0 XRP';

  // For very small prices (less than 1 drop), show more decimal places
  if (price > 0 && price < 0.000001) {
    // Find how many significant digits we need
    const exponent = Math.floor(Math.log10(price));
    const significantDigits = Math.max(2, -exponent + 1);
    const formatted = price.toFixed(Math.min(significantDigits, 10)).replace(/\.?0+$/, '');
    return `${formatted} XRP`;
  }

  // Round to 6 decimal places (XRP's smallest unit is 1 drop = 0.000001 XRP)
  const rounded = Math.round(price * 1_000_000) / 1_000_000;

  // If it's an integer, show without decimals or separators
  if (Number.isInteger(rounded)) {
    return `${rounded} XRP`;
  }

  // For decimals, remove trailing zeros
  const formatted = rounded.toString().replace(/\.?0+$/, '');

  return `${formatted} XRP`;
}

/**
 * Format XRP floor price capped at 2 decimal places, trailing zeros stripped.
 * - 5.205942 → "5.21 XRP"
 * - 199 → "199 XRP"
 * - 0.5 → "0.5 XRP"
 */
export function formatFloorPrice(price: number | null | undefined): string {
  if (price === null || price === undefined) return 'N/A';
  if (price === 0) return '0 XRP';

  const rounded = Math.round(price * 100) / 100;

  if (Number.isInteger(rounded)) {
    return `${rounded} XRP`;
  }

  const formatted = rounded.toString().replace(/\.?0+$/, '');
  return `${formatted} XRP`;
}

/**
 * Format XRP price rounded to nearest tenth (1 decimal place)
 * Used for compact displays like trait cards where precision isn't critical
 * - 295.233 → "295.2 XRP"
 * - 207 → "207 XRP"
 * - 199.95 → "200 XRP"
 */
export function formatPriceRounded(price: number | null | undefined): string {
  if (!price) return 'N/A';

  // Round to 1 decimal place
  const rounded = Math.round(price * 10) / 10;

  // If it's an integer, show without decimals
  if (Number.isInteger(rounded)) {
    return `${rounded} XRP`;
  }

  return `${rounded} XRP`;
}

/**
 * Format a number (without XRP suffix) with proper decimal handling
 * Useful when you need the number formatted but want to add your own units/suffix
 */
export function formatNumber(value: number): string {
  // If it's an integer, show without decimals
  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }

  // For decimals, remove trailing zeros
  const formatted = value.toString().replace(/\.?0+$/, '');

  // Add thousand separators
  const parts = formatted.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return parts.join('.');
}

/**
 * Format volume with K/M abbreviations
 * Note: Unlike formatPrice, volume of 0 should display as "0 XRP" not "N/A"
 */
export function formatVolume(volume: number | null | undefined): string {
  if (volume === null || volume === undefined) {
    return 'N/A';
  }
  if (volume >= 1000000) {
    return `${formatNumber(Math.round(volume / 10000) / 100)}M XRP`;
  } else if (volume >= 1000) {
    return `${formatNumber(Math.round(volume / 10) / 100)}K XRP`;
  } else if (volume === 0) {
    return '0 XRP';
  }
  return `${Math.round(volume)} XRP`;
}

/**
 * Format XRPL address to show first 8 and last 6 characters
 * Example: rN7n7otQDd6FczFgLdlLrCNvgZGnqxQYXZ → rN7n7otQ...nqxQYXZ
 */
export function formatAddress(address: string): string {
  if (address.length <= 14) return address;
  return `${address.slice(0, 8)}...${address.slice(-6)}`;
}

/**
 * Rarity tier configuration for NFT traits
 * Based on percentage of collection that has this trait
 */
export type RarityTier = 'legendary' | 'epic' | 'rare' | 'uncommon' | 'common';

export interface RarityInfo {
  tier: RarityTier;
  label: string;
  color: string;
  bgColor: string;
}

const RARITY_TIERS: { maxPercent: number; info: RarityInfo }[] = [
  { maxPercent: 1, info: { tier: 'legendary', label: 'Legendary', color: '#fbbf24', bgColor: 'transparent' } },
  { maxPercent: 5, info: { tier: 'epic', label: 'Epic', color: '#a78bfa', bgColor: 'transparent' } },
  { maxPercent: 15, info: { tier: 'rare', label: 'Rare', color: '#60a5fa', bgColor: 'transparent' } },
  { maxPercent: 35, info: { tier: 'uncommon', label: 'Uncommon', color: '#4ade80', bgColor: 'transparent' } },
  { maxPercent: 100, info: { tier: 'common', label: 'Common', color: '#9ca3af', bgColor: 'transparent' } },
];

/**
 * Get rarity tier info based on trait percentage
 * - Legendary: < 1% (found in less than 1% of collection)
 * - Epic: 1-5%
 * - Rare: 5-15%
 * - Uncommon: 15-35%
 * - Common: > 35%
 */
export function getRarityInfo(percentage: number | null | undefined): RarityInfo {
  if (percentage === null || percentage === undefined) {
    return RARITY_TIERS[RARITY_TIERS.length - 1].info; // Default to common
  }

  for (const tier of RARITY_TIERS) {
    if (percentage < tier.maxPercent) {
      return tier.info;
    }
  }

  return RARITY_TIERS[RARITY_TIERS.length - 1].info;
}

/**
 * Get rarity tier based on a trait's rank within its category.
 *
 * Instead of fixed percentage thresholds (which break when all traits in a
 * category have low percentages, e.g., composable collections), this ranks
 * the trait value among its siblings and distributes tiers by percentile:
 *   - Bottom 10% (rarest values) → Legendary
 *   - 10-25% → Epic
 *   - 25-50% → Rare
 *   - 50-75% → Uncommon
 *   - Top 25% (most common) → Common
 *
 * @param percentage - This trait value's percentage
 * @param allPercentages - All percentages in the same trait category (sorted or unsorted)
 */
export function getRarityInfoInCategory(
  percentage: number | null | undefined,
  allPercentages: number[]
): RarityInfo {
  if (percentage === null || percentage === undefined || allPercentages.length === 0) {
    return RARITY_TIERS[RARITY_TIERS.length - 1].info;
  }

  // If only 1-2 values in category, use absolute thresholds (not enough to rank)
  if (allPercentages.length <= 2) {
    return getRarityInfo(percentage);
  }

  // Sort ascending (rarest first)
  const sorted = [...allPercentages].sort((a, b) => a - b);

  // If all values are the same (no variation), there's no meaningful
  // rarity distinction within the category — label as Common
  if (sorted[0] === sorted[sorted.length - 1]) {
    return RARITY_TIERS[RARITY_TIERS.length - 1].info;
  }

  // Find this value's position (0-based index of first match)
  const rank = sorted.indexOf(percentage);
  // Convert to percentile (0 = rarest, 100 = most common)
  const percentile = (rank / (sorted.length - 1)) * 100;

  if (percentile < 10) return RARITY_TIERS[0].info;       // Legendary
  if (percentile < 25) return RARITY_TIERS[1].info;       // Epic
  if (percentile < 50) return RARITY_TIERS[2].info;       // Rare
  if (percentile < 75) return RARITY_TIERS[3].info;       // Uncommon
  return RARITY_TIERS[4].info;                             // Common
}

/**
 * Create a rarity lookup function from a collection's trait_stats.
 * Precomputes all category percentages so getRarityInfoInCategory can be
 * called efficiently for each trait without re-sorting every time.
 *
 * Usage:
 *   const lookup = createTraitRarityLookup(collection.trait_stats);
 *   const info = lookup(traitType, percentage);
 */
export function createTraitRarityLookup(
  traitStats: Record<string, Record<string, { percentage: number }>> | null | undefined
): (traitType: string, percentage: number | null | undefined) => RarityInfo {
  if (!traitStats) return (_t, p) => getRarityInfo(p);

  const categoryPercentages: Record<string, number[]> = {};
  for (const [traitType, values] of Object.entries(traitStats)) {
    categoryPercentages[traitType] = Object.values(values).map(v => v.percentage);
  }

  return (traitType: string, percentage: number | null | undefined) => {
    const allPercentages = categoryPercentages[traitType];
    if (!allPercentages) return getRarityInfo(percentage);
    return getRarityInfoInCategory(percentage, allPercentages);
  };
}

/**
 * Get rarity tier info based on NFT rank within a collection
 * Converts rank to percentile and uses the same tier system as traits
 *
 * @param rank - The NFT's rarity rank (1 = rarest)
 * @param totalCount - Total number of NFTs in the collection
 * @returns RarityInfo with tier, label, and colors
 *
 * Examples:
 * - Rank 5 of 1000 = top 0.5% = Legendary
 * - Rank 30 of 1000 = top 3% = Epic
 * - Rank 100 of 1000 = top 10% = Rare
 */
export function getRarityInfoByRank(rank: number, totalCount: number): RarityInfo {
  if (totalCount <= 0 || rank <= 0) {
    return RARITY_TIERS[RARITY_TIERS.length - 1].info; // Default to common
  }
  const percentile = (rank / totalCount) * 100;
  return getRarityInfo(percentile);
}
